import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getSession();
      setSession(data.session ?? null);
      setLoading(false);
    })();
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  // Load profile whenever session changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!session?.user?.id) { setProfile(null); setIsAdmin(false); return; }
      // Fetch profile and admin flag independently so the app keeps working even
      // before the admin_users table migration has been applied on Supabase.
      const [profRes, adminRes] = await Promise.allSettled([
        supabase.from("profiles").select("*").eq("id", session.user.id).maybeSingle(),
        supabase.from("admin_users").select("user_id").eq("user_id", session.user.id).maybeSingle(),
      ]);
      if (cancelled) return;
      setProfile(profRes.status === "fulfilled" ? (profRes.value.data ?? null) : null);
      setIsAdmin(
        adminRes.status === "fulfilled" && !adminRes.value.error && !!adminRes.value.data
      );
    })();
    return () => { cancelled = true; };
  }, [session]);

  const signIn = useCallback(async ({ email, password }) => {
    return supabase.auth.signInWithPassword({ email, password });
  }, []);

  const signUp = useCallback(async ({ email, password, account_type, full_name, org_name }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { account_type, full_name } },
    });
    if (error) return { data, error };
    // If Supabase email-confirmation is ON, data.session is null: no writes possible under RLS.
    const hasSession = !!data.session;
    if (hasSession && data.user) {
      if (account_type === "ORGANIZATION" && org_name) {
        const { data: org, error: orgErr } = await supabase.from("organizations").insert({ name: org_name }).select().single();
        if (!orgErr && org) {
          await supabase.from("profiles").update({ organization_id: org.id }).eq("id", data.user.id);
        }
      }
      if (full_name) {
        await supabase.from("profiles").update({ full_name }).eq("id", data.user.id);
      }
    }
    return { data, error: null, needsConfirmation: !hasSession };
  }, []);

  const resetPassword = useCallback(async (email) => {
    return supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/`,
    });
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null); setProfile(null); setIsAdmin(false);
  }, []);

  const refreshProfile = useCallback(async () => {
    if (!session?.user?.id) return;
    const [profRes, adminRes] = await Promise.allSettled([
      supabase.from("profiles").select("*").eq("id", session.user.id).maybeSingle(),
      supabase.from("admin_users").select("user_id").eq("user_id", session.user.id).maybeSingle(),
    ]);
    setProfile(profRes.status === "fulfilled" ? (profRes.value.data ?? null) : null);
    setIsAdmin(
      adminRes.status === "fulfilled" && !adminRes.value.error && !!adminRes.value.data
    );
  }, [session]);

  return (
    <AuthCtx.Provider value={{ session, user: session?.user ?? null, profile, isAdmin, loading, signIn, signUp, signOut, resetPassword, refreshProfile }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
